import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import Button, { ButtonSize, ButtonVariant } from '@components/DesignSystem/Button'
import { useArticleContext } from '../_core'
import PublishSettingContainer from './PublishSettingContainer'
import tw, { styled, theme } from 'twin.macro'
import PublishSettingLightbox from './PublishSettingLightbox'
import PublishButton from './PublishButton'
import { withRNApp } from '@libs/RNApp'

export enum SettingVisibilityType {
  HIDE = 'hide',
  SHOW = 'show',
  PENDING = 'pending',
}

type PublishSettingButtonProps = {
  isPublished: boolean
}

const PublishSettingButton = styled(Button)<PublishSettingButtonProps>`
  ${(props) => props.isPublished && tw`hidden md:inline-flex`}
`

const MobilePublishButton = styled(PublishButton)`
  ${tw`md:hidden`}
`

const PublishSetting = () => {
  const { t } = useTranslation('editor')
  const [settingVisibility, setSettingVisibility] = useState(SettingVisibilityType.HIDE)
  const {
    articleState: {
      status,
      publish: { status: publishButtonStatus, warnings },
    },
    PublishButtonStatusType,
    checkInputWarnings,
    PublishStatusType,
  } = useArticleContext()
  const publishButtonDisabled = publishButtonStatus === PublishButtonStatusType.DISABLED

  const [isLightboxVisible, setIsLightboxVisible] = useState(false)
  const openLightbox = () => setIsLightboxVisible(true)
  const closeLightbox = () => setIsLightboxVisible(false)
  const isPublished = status === PublishStatusType.PUBLISHED

  const publishSettingButtonOnClick = () => {
    const warnings = checkInputWarnings()
    if (warnings) return

    if (window.matchMedia(`(min-width: ${theme`screens.md`})`).matches) {
      return setSettingVisibility(SettingVisibilityType.PENDING)
    }

    openLightbox()
  }

  useEffect(() => {
    if (settingVisibility !== SettingVisibilityType.PENDING) return
    if (warnings) {
      for (const key in warnings) {
        if (warnings[key]) {
          document.documentElement.scrollTop = 0
          setSettingVisibility(SettingVisibilityType.HIDE)
          return
        }
      }
      setSettingVisibility(SettingVisibilityType.SHOW)
    } else {
      setSettingVisibility(SettingVisibilityType.SHOW)
    }
  }, [warnings, settingVisibility])

  const closePublishSetting = useCallback(() => {
    setSettingVisibility(SettingVisibilityType.HIDE)
  }, [setSettingVisibility])

  return (
    <>
      <PublishSettingButton
        disabled={publishButtonDisabled}
        size={ButtonSize.block}
        variant={ButtonVariant.primary}
        onClick={publishSettingButtonOnClick}
        isPublished={isPublished}
      >
        {isPublished ? t('aside.publish/setting') : t('aside.publish/schedule')}
      </PublishSettingButton>
      {isPublished && <MobilePublishButton />}
      {settingVisibility === SettingVisibilityType.SHOW && (
        <PublishSettingContainer onClose={closePublishSetting} />
      )}
      {isLightboxVisible && <PublishSettingLightbox close={closeLightbox} />}
    </>
  )
}

export default withRNApp(PublishSetting)
